import { PayType } from '~/domain/PermanentJobs/enums';
import { expectedDaysOptions } from '~/components/PermanentJobsV2/utils';
import { TNullable } from '~/types/Nullable';

/**
 * Format pay title based on rate values
 * - "Stated Pay Range": when minRate !== maxRate
 * - "Stated Pay": when minRate === maxRate or only one rate exists
 */
export function formatPayTitle(minRate: TNullable<number>, maxRate: TNullable<number>): string {
  // If both rates exist and are different, it's a range
  if (minRate != null && maxRate != null && minRate !== maxRate) {
    return 'Stated Pay Range';
  }

  // Otherwise it's fixed pay
  return 'Stated Pay';
}

/**
 * Format pay range for display from domain model (numbers)
 */
export function formatPayRange(
  minRate: TNullable<number>,
  maxRate: TNullable<number>,
  payType: TNullable<PayType>,
): string {
  if (minRate == null && maxRate == null) return '';

  const suffix = payType === PayType.SALARY ? '/year' : '/hour';

  // Format with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // If both rates exist and are different, show range
  if (minRate != null && maxRate != null && minRate !== maxRate) {
    return `$${formatNumber(minRate)} - $${formatNumber(maxRate)}${suffix}`;
  }

  // Otherwise show single value (prefer minRate)
  const rate = minRate ?? maxRate;
  return rate != null ? `$${formatNumber(rate)}${suffix}` : '';
}

/**
 * Format benefits for display
 */
export function formatBenefits(benefits: string[]): string {
  if (!benefits || benefits.length === 0) return '';

  return benefits.join(', ');
}

/**
 * Format expected days for display
 */
export function formatExpectedDays(expectedDays: number[]): string {
  if (!expectedDays || expectedDays.length === 0) return '';

  // Sort days to maintain order (Monday first)
  const sortedDays = [...expectedDays].sort((a, b) => {
    // Convert Sunday (0) to 7 for proper sorting
    const dayA = a === 0 ? 7 : a;
    const dayB = b === 0 ? 7 : b;
    return dayA - dayB;
  });

  const dayNames = sortedDays.map((dayCode) => {
    const dayOption = expectedDaysOptions.find((option) => option.code === dayCode);
    return dayOption?.name || '';
  });

  return dayNames.filter(Boolean).join(', ');
}
